function asCentavos(value) {
  return Math.max(0, Math.round(Number(value || 0)));
}

function itemIdOf(item) {
  return item?.treatment_plan_item_id || item?.item_id || null;
}

export function dentureBillingItems(billingState) {
  if (!billingState?.denture_item_billing) return [];
  return (Array.isArray(billingState?.denture_items) ? billingState.denture_items : [])
    .map((item, index) => ({
      ...item,
      treatment_plan_item_id: itemIdOf(item),
      fee_centavos: asCentavos(item?.fee_centavos),
      paid_centavos: asCentavos(item?.paid_centavos),
      balance_centavos: asCentavos(item?.balance_centavos),
      suggested_next_centavos: asCentavos(item?.suggested_next_centavos),
      _billing_order: index,
    }))
    .filter((item) => item.treatment_plan_item_id);
}

export function currentDentureItemIds(planDoneItems = []) {
  return Array.from(new Set(
    planDoneItems
      .map((item) => item?.treatment_plan_item_id || item?.item_id || null)
      .filter(Boolean),
  ));
}

export function dentureItemLabel(item) {
  const arch = String(item?.arch || "").toLowerCase();
  if (arch === "upper") return "Upper denture";
  if (arch === "lower") return "Lower denture";
  return "Denture";
}

export function isDentureItemPaidInFull(item) {
  const balance = Number(item?.balance_centavos);
  return Number.isFinite(balance) && balance <= 0;
}

// Returns the exact plan-item targets sent to complete_checkout_core.
// Due-now targets always win: deposit shortfalls and completed-item delivery
// balances are consumed before any flexible advance payment. Within each pass,
// item(s) on the current appointment are newest first, followed by every other
// denture newest first. A routine in-progress visit therefore cannot steal a
// completed sibling denture's delivery payment merely because both were
// handled on the same appointment.
// Manual mode never silently fixes the user's split: validation reports a
// mismatch so checkout can block before a financial write is attempted.
export function buildDenturePaymentAllocation({
  billingState,
  amountCentavos = 0,
  currentItemIds = [],
  manualAmounts = null,
} = {}) {
  const items = dentureBillingItems(billingState);
  const payableItems = items.filter((item) => !isDentureItemPaidInFull(item));
  const totalBalance = items.reduce((sum, item) => sum + item.balance_centavos, 0);
  const requested = Math.min(asCentavos(amountCentavos), totalBalance);
  const current = new Set(currentItemIds.filter(Boolean));
  const ordered = [
    ...payableItems.filter((item) => current.has(item.treatment_plan_item_id)).reverse(),
    ...payableItems.filter((item) => !current.has(item.treatment_plan_item_id)).reverse(),
  ];

  let targets;
  if (manualAmounts && typeof manualAmounts === "object") {
    // Paid-in-full rows are display-only. Ignore stale/manual values for them
    // so Checkout never sends an allocation that the database must reject.
    targets = payableItems.map((item) => ({
      plan_id: item.treatment_plan_id || billingState?.plan_id || null,
      treatment_plan_item_id: item.treatment_plan_item_id,
      amount_centavos: asCentavos(manualAmounts[item.treatment_plan_item_id]),
    })).filter((target) => target.amount_centavos > 0);
  } else {
    let remaining = requested;
    targets = [];

    // First consume only the database's due-now suggestion for each item.
    // This is the per-item 50/50 contract: deposit_due or delivery_due may be
    // collected now, while an in-progress item with its deposit already met
    // remains at zero due.
    for (const item of ordered) {
      if (remaining <= 0) break;
      const amount = Math.min(
        item.balance_centavos,
        item.suggested_next_centavos,
        remaining,
      );
      if (amount > 0) {
        targets.push({
          plan_id: item.treatment_plan_id || billingState?.plan_id || null,
          treatment_plan_item_id: item.treatment_plan_item_id,
          amount_centavos: amount,
        });
        remaining -= amount;
      }
    }

    // Collection is still flexible. If staff tenders more than the sum due
    // now, allocate that advance using the current/newest order without ever
    // exceeding an item's remaining balance.
    for (const item of ordered) {
      if (remaining <= 0) break;
      const existing = targets.find(
        (target) => target.treatment_plan_item_id === item.treatment_plan_item_id,
      );
      const available = Math.max(
        0,
        item.balance_centavos - Number(existing?.amount_centavos || 0),
      );
      const amount = Math.min(available, remaining);
      if (amount <= 0) continue;
      if (existing) {
        existing.amount_centavos += amount;
      } else {
        targets.push({
          plan_id: item.treatment_plan_id || billingState?.plan_id || null,
          treatment_plan_item_id: item.treatment_plan_item_id,
          amount_centavos: amount,
        });
      }
      remaining -= amount;
    }
  }

  const targetTotal = targets.reduce((sum, target) => sum + target.amount_centavos, 0);
  const overItemBalance = targets.some((target) => {
    const item = items.find((candidate) => candidate.treatment_plan_item_id === target.treatment_plan_item_id);
    return !item || target.amount_centavos > item.balance_centavos;
  });
  const valid = targetTotal === requested && !overItemBalance;

  return {
    items,
    requested_centavos: requested,
    target_total_centavos: targetTotal,
    targets,
    valid,
    error: overItemBalance
      ? "A denture cannot receive more than its remaining balance."
      : targetTotal !== requested
        ? "Choose how to divide the full payment between the unpaid dentures."
        : null,
  };
}

export function defaultDentureManualAmounts(allocation) {
  return Object.fromEntries(
    (allocation?.targets || []).map((target) => [
      target.treatment_plan_item_id,
      target.amount_centavos,
    ]),
  );
}

// A front-desk "finish and bill" choice is case-level intent, not a series of
// item clicks. The database consumes these fields in one checkout transaction:
// it records every still-open denture against today's appointment, posts the
// payment allocations, then closes the MV. Keeping this payload builder here
// prevents the UI and submit path from inventing different finish rules.
export function buildBundleFinishFields({
  planId = null,
  planIsBundle = false,
  finishSelected = false,
  hasOpenDentureItems = false,
  arReason = "",
} = {}) {
  if (!planId || !planIsBundle || !finishSelected) return {};

  const reason = String(arReason || "").trim();
  return {
    finish_bundle_plan_id: planId,
    finish_bundle_complete_open_dentures: hasOpenDentureItems || undefined,
    finish_bundle_ar_reason: reason || undefined,
  };
}
